"use client";
import Icon from "@/components/atoms/Icons";
import Image from "@/components/atoms/image";
import { motion } from "framer-motion";
import Heading from "@/components/molecules/Heading";
import { ImageData } from "@/page-builder/fragments/StatsCardsBlock/types";

export interface GolsGridProps {
  title: string;
  items: {
    title: string;
    theme: string;
    image: ImageData;
  }[];
}

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.2,
      ease: "easeOut",
    },
  }),
};

const getCard = (
  style: "Red" | "orange" | "greenDark" | "greenLight",
  title: string,
  url: string,
  alt: string,
  key: string
) => {
  switch (style) {
    case "orange":
      return (
        <motion.div
          key={key}
          variants={fadeUpVariants as any}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
          className="relative overflow-hidden col-span-2 md:col-span-1 flex w-full xl:min-h-[360px]
                       md:min-h-[300px] min-h-[276px]"
        >
          <div className="bg-[#FEF8F6] flex flex-col w-full">
            <div className=" pt-[44px] pb-[44px] pl-[32px] pr-[32px] xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]  gap-[8px] flex flex-col justify-end flex-1">
              <span className="text-neutral-900 text-xl-bold xl:text-2xl-bold leading-[36px] xl:leading-[48px]">
                {title}
              </span>
            </div>
            <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
              <Icon name="fig7" color="#E16F60" />
            </div>
            <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
              {url && <Image fill sizes="50px" alt={""} src={`${url}`} />}
            </div>
            <div className="absolute bottom-[-8px] left-[32px] w-[153px] h-[43px]">
              <Icon color="#FADACC" name="fig8" />
            </div>
          </div>
        </motion.div>
      );
    case "greenLight":
      return (
        <motion.div
          key={key}
          variants={fadeUpVariants as any}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
          className="relative col-span-2 md:col-span-1 flex w-full xl:min-h-[360px]
                       md:min-h-[300px] min-h-[276px] overflow-hidden"
        >
          <div className="bg-[#CFE5E0] flex flex-col w-full">
            <div className=" pt-[44px] pb-[44px] pl-[32px] pr-[32px] xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]  gap-[8px] flex flex-col justify-end flex-1">
              <span className="text-neutral-900 text-xl-bold xl:text-2xl-bold leading-[36px] xl:leading-[48px]">
                {title}
              </span>
            </div>
            <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
              <Icon name="fig7" color="#0E8668" />
            </div>
            <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
              {url && <Image fill sizes="50px" alt={""} src={`${url}`} />}
            </div>
            <div className="absolute bottom-[-6px] right-[-10px] w-[206px] h-[39px]">
              <Icon name="fig9" color="#BFD9C8" />
            </div>
          </div>
        </motion.div>
      );
    case "greenDark":
      return (
        <motion.div
          key={key}
          variants={fadeUpVariants as any}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={2}
          className="relative col-span-2 md:col-span-1 flex w-full xl:min-h-[360px]
                       md:min-h-[300px] min-h-[276px] overflow-hidden"
        >
          <div className="bg-[#BFD9C8] flex flex-col w-full">
            <div className=" pt-[44px] pb-[44px] pl-[32px] pr-[32px] xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]  gap-[8px] flex flex-col justify-end flex-1">
              <span className="text-neutral-900 text-xl-bold xl:text-2xl-bold leading-[36px] xl:leading-[48px]">
                {title}
              </span>
            </div>
            <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
              <Icon name="fig7" color="#0C735A" />
            </div>
            <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
              {url && <Image fill sizes="50px" alt={""} src={`${url}`} />}
            </div>
            <div className="absolute bottom-[-6px] right-[-10px] w-[206px] h-[39px]">
              <Icon name="fig9" color="#CFE5E0" />
            </div>
          </div>
        </motion.div>
      );
    default:
      return (
        <motion.div
          key={key}
          variants={fadeUpVariants as any}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={3}
          className="relative col-span-2 md:col-span-1 flex w-full xl:min-h-[360px]
                       md:min-h-[300px] min-h-[276px] overflow-hidden"
        >
          <div className="bg-[#FDEEE8] flex flex-col w-full">
            <div className=" pt-[44px] pb-[44px] pl-[32px] pr-[32px] xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]  gap-[8px] flex flex-col justify-end flex-1">
              <span className="text-neutral-900 text-xl-bold xl:text-2xl-bold leading-[36px] xl:leading-[48px]">
                {title}
              </span>
            </div>
            <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
              <Icon name="fig7" color="#F6B89B" />
            </div>
            <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
              {url && <Image fill sizes="50px" alt={""} src={`${url}`} />}
            </div>
            <div className="absolute bottom-[-8px] left-[32px] w-[153px] h-[43px]">
              <Icon color="#FADACC" name="fig8" />
            </div>
          </div>
        </motion.div>
      );
  }
};

export const GolsGrid = ({ items = [], title = "" }: GolsGridProps) => {
  return (
    <div className="container mx-auto">
      <div className="grid grid-cols-6 gap-[32px]">
        <div className="col-span-6 xl:md:col-span-2 max-w-full xl:max-w-[220px]">
          <Heading title={title} />
        </div>
        <div className="grid grid-cols-2 col-span-6 md:col-span-6 xl:col-span-4 gap-[32px] overflow-hidden">
          {items.map((e, i) =>
            getCard(
              e.theme as "Red" | "orange" | "greenDark" | "greenLight",
              e.title,
              e.image.url,
              e.image.alt,
              i + ""
            )
          )}
        </div>
      </div>
    </div>
  );
};
